/**
 * Near-Me Review Schemas
 * ======================
 * A rating + optional text/photos left by a seeker on a business (one review
 * per user per business). Owners may respond once. The list endpoint attaches
 * the business's rating aggregate.
 */

import { z } from 'zod';

const objectIdSchema = z.string().regex(/^[a-f\d]{24}$/i, 'Invalid id');

const emptyToUndefined = (v: unknown): unknown =>
  typeof v === 'string' && v.trim() === '' ? undefined : v;

/** Create (or update) a review. */
export const createReviewBodySchema = z.object({
  businessId: objectIdSchema,
  rating: z.number().int().min(1).max(5),
  text: z.preprocess(emptyToUndefined, z.string().trim().max(1000).optional()),
  photos: z.array(z.string().trim().url()).max(5).default([]),
});
export type CreateReviewInput = z.infer<typeof createReviewBodySchema>;

/** Owner's single response to a review. */
export const ownerRespondBodySchema = z.object({
  response: z.string().trim().min(1).max(1000),
});
export type OwnerRespondInput = z.infer<typeof ownerRespondBodySchema>;

/** Reviews list query. */
export const reviewsQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});
export type ReviewsQuery = z.infer<typeof reviewsQuerySchema>;

// ─── Wire types ──────────────────────────────────────────────────────────────

export interface NearMeReview {
  _id: string;
  businessId: string;
  userId: string;
  userName: string;
  rating: number;
  text?: string;
  photos: string[];
  ownerResponse?: string;
  ownerRespondedAt?: string;
  helpfulCount: number;
  createdAt: string;
  updatedAt: string;
}

/** Paginated reviews + the business rating aggregate. */
export interface NearMeReviewListResult {
  items: NearMeReview[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  ratingAvg: number;
  ratingCount: number;
}
